import themeColors from "@GeriMed/styles/variables";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import translate from "@insite/client-framework/Translate";
import { AutocompleteItemModel, ProductAutocompleteItemModel } from "@insite/client-framework/Types/ApiModels";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden from "@insite/mobius/Hidden";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import React from "react";
import { css } from "styled-components";

interface Props {
    products: ProductAutocompleteItemModel[];
    focusedItem?: AutocompleteItemModel;
    goToUrl: (url: string) => void;
    extendedStyles?: AutocompleteProductsStyles;
}

export interface AutocompleteProductsStyles {
    headerText?: TypographyPresentationProps;
    container?: GridContainerProps;
    focusedContainer?: GridContainerProps;
    imageGridItem?: GridItemProps;
    image?: LazyImageProps;
    infoGridItem?: GridItemProps;
    titleLink?: LinkPresentationProps;
    erpNumberText?: TypographyPresentationProps;
}

export const autocompleteProductsStyles: AutocompleteProductsStyles = {
    headerText: {
        css: css`
            font-size: 14px;
            line-height: 22px;
            color: #7d7d7d;
            font-style: italic;
            text-align: left;
            margin: 10px 0 5px 0;
        `,
    },
    container: {
        gap: 0,
        css: css`
            flex-grow: 0;
            margin: 5px 0;
            cursor: pointer;

            &.autocomplete-products-container:hover {
                text-decoration: none;
                background: ${themeColors.lightGray};
            }

            .autocomplete-products-image {
                min-height: 42px;
                min-width: 45px;
            }

            .autocomplete-products-image-grid-item {
                padding: 5px 10px;
                box-sizing: border-box;
            }

            &.autocomplete-products-container {
                padding: 0;
                margin: 0;
            }
            .search-formulary:before {
                vertical-align: middle;
                font-size: 25px;
                line-height: 1.25em;
                padding-right: 0;
                content: "\\e64b";
                font-family: "Geriatric-Medical";
                color: ${themeColors.brightGreen};
                padding-left: 10px;
            }
            .search-purchase-history:before {
                vertical-align: middle;
                font-size: 25px;
                line-height: 1.25em;
                padding-right: 0;
                content: "\\e64a";
                font-family: "Geriatric-Medical";
                color: ${themeColors.heavyBlue2};
                padding-left: 10px;
            }
            .search-equipment-formulary {
                background: url(/userfiles/images/office-supplies.png) no-repeat -1px 1px;
                display: inline-block;
                vertical-align: middle;
                height: 30px;
                width: 30px;
                margin-left: 7px;
                mix-blend-mode: multiply;
                @media print {
                    -webkit-print-color-adjust: exact;
                }
            }
            .search-medical-equipment {
                background: url(/userfiles/images/medical-equipment.png) no-repeat -2px -3px;
                display: inline-block;
                vertical-align: middle;
                height: 30px;
                width: 30px;
                margin-left: 8px;
                mix-blend-mode: multiply;
                @media print {
                    -webkit-print-color-adjust: exact;
                }
            }
        `,
    },
    focusedContainer: {
        gap: 0,
        css: css`
            flex-grow: 0;
            margin: 5px 0;
            cursor: pointer;
            background-color: lightgray;
        `,
    },
    imageGridItem: {
        width: 3,
        css: css`
            max-width: 65px;
            max-height: 50px;
            flex-basis: 65px;
        `,
    },
    image: {
        width: "50px",
        css: css`
            img {
                height: 100%;
            }
        `,
    },
    infoGridItem: {
        width: 9,
        css: css`
            display: block;
        `,
    },
    titleLink: {
        typographyProps: {
            ellipsis: true,
            css: css`
                width: 100%;
                text-align: left;
                margin: 0;
            `,
        },
        css: css`
            // width: 270px;
            &.autocomplete-products-link {
                &:hover span {
                    color: #252525;
                }
                span {
                    font-size: 14px;
                    color: #252525;

                    b {
                        color: #1c3e95;
                        font-weight: 100;
                    }
                }
            }
        `,
    },
    erpNumberText: {
        ellipsis: true,
        css: css`
            // width: 270px;
            text-align: left;
            margin: 5px 0 0 0;
        `,
    },
};

const styles = autocompleteProductsStyles;

class AutocompleteProducts extends React.Component<Props> {
    private readonly styles: AutocompleteProductsStyles;

    constructor(props: Props) {
        super(props);

        this.styles = mergeToNew(styles, props.extendedStyles);
    }

    render() {
        const { products } = this.props;
        const styles = this.styles;

        return (
            <>
                <Hidden above="md">
                    <Typography
                        {...styles.headerText}
                        data-test-selector="autocompleteProducts_Header"
                        className="autocomplete-products-header"
                    >
                        {translate("Products")}
                    </Typography>
                </Hidden>
                {products.map(product => (
                    <GridContainer
                        {...(this.props.focusedItem === product ? styles.focusedContainer : styles.container)}
                        key={product.id!}
                        onClick={() => {
                            this.props.goToUrl(product.url);
                        }}
                        className="autocomplete-products-container"
                    >
                        <GridItem {...styles.imageGridItem} className="autocomplete-products-image-grid-item">
                            <LazyImage {...styles.image} src={product.image} className="autocomplete-products-image" />
                        </GridItem>
                        <GridItem {...styles.infoGridItem} className="autocomplete-products-info-grid-item">
                            <Link
                                {...styles.titleLink}
                                data-test-selector={`productId_${product.id}`}
                                className="autocomplete-products-link"
                            >
                                {product.displayTitle}
                            </Link>
                            <Typography>
                                {product.properties != null && product.properties["onFormulary"] === "true" && (
                                    <span className="search-formulary">&nbsp;</span>
                                )}
                                {product.properties != null &&
                                    product.properties != null &&
                                    product.properties["onPurchaseHistory"] === "true" && (
                                        <span className="search-purchase-history">&nbsp;</span>
                                    )}
                                {product.properties != null &&
                                    product.properties["onEquipmentFormulary"] === "true" && (
                                        <span className="search-equipment-formulary">&nbsp;</span>
                                    )}
                                {product.properties != null && product.properties["onMedicalEquipment"] === "true" && (
                                    <span className="search-medical-equipment">&nbsp;</span>
                                )}
                            </Typography>
                            {/* <Typography
                                {...styles.erpNumberText}
                                data-test-selector="autocompleteProducts_PartNumber"
                                className="autocomplete-products-erp-number"
                            >
                                {product.isNameCustomerOverride ? (
                                    <>
                                        {translate("My Part #")}
                                        {product.displayName}
                                    </>
                                ) : (
                                    <>
                                        {translate("Part #")}
                                        {product.displayErpNumber}
                                    </>
                                )}
                                {product.manufacturerItemNumber && (
                                    <>
                                        &nbsp;
                                        {translate("MFG #")}
                                        {product.displayManufacturerItemNumber}
                                    </>
                                )}
                            </Typography> */}
                        </GridItem>
                    </GridContainer>
                ))}
            </>
        );
    }
}

export default AutocompleteProducts;
